import { ensureVisitorId } from '@/ab-lib/visitor-id';
import type { IHttpClient } from '../client';
import {
    AI_FIELD_SELECTOR,
    ENDPOINT_GATE,
    FORM_DATA_KEY,
    FORM_FIELD,
    GATE_TIMEOUT_MS,
    STORAGE_KEY,
} from './const';

export type TTrialGate = {
    arm: string;
    // today's free generation is used: the drawer opens on the sign-in offer
    spent: boolean;
    // no answer (network, timeout, non-200): outside the test for now, but the
    // init goal says so – a breakage equal in both arms keeps the 50/50 balance
    failed?: boolean;
};

const FAILED: TTrialGate = { arm: '', spent: false, failed: true };

let pending: Promise<TTrialGate> | null = null;

/**
 * The anonymous-trial arm of this visitor, '' – outside the test. Asked when a
 * visitor not signed in taps the AI button: the cached HTML can't carry it.
 * Asked on every tap, not once per page: `spent` changes with a generation on
 * this very page. A failed or hung request means the sign-in offer for now.
 */
export function askTrialGate(http: IHttpClient): Promise<TTrialGate> {
    if (!pending) {
        // The server decides by the `visitor_id` cookie – the same one
        // `Split::decide()` reads when the card is saved.
        ensureVisitorId();

        let timer: ReturnType<typeof setTimeout> | undefined;
        const request = Promise.race([
            http.get(ENDPOINT_GATE, {}).then((res): TTrialGate => {
                if (res.status !== 200 || res.body.ok !== true) {
                    throw new Error(`ai greeting gate: HTTP ${res.status}`);
                }

                return {
                    arm: typeof res.body.arm === 'string' ? res.body.arm : '',
                    spent: res.body.spent === true,
                };
            }),
            // a hung request must not leave the trigger disabled
            new Promise<TTrialGate>((_, reject) => {
                timer = setTimeout(
                    () => reject(new Error('ai greeting gate: timeout')),
                    GATE_TIMEOUT_MS
                );
            }),
        ]).finally(() => {
            clearTimeout(timer);
            // only an in-flight request is shared (the content and caption buttons)
            if (pending === request) {
                pending = null;
            }
        });
        pending = request;
    }

    return pending.catch(() => FAILED);
}

/**
 * The exposure point of the test: the arm the gate answered rides with the card
 * the visitor creates, and only such a card joins the cohort (`exposedByForm`).
 * Kept in the tab's storage too: signing in reloads the page, and an exposure
 * lost there would drop from the cohort exactly the control visitors who signed
 * in to get the AI – a different share in each arm, so a biased comparison.
 */
export function rememberTrialArm(form: HTMLFormElement | null, arm: string): void {
    if (!arm) return;

    if (form) {
        form.dataset[FORM_DATA_KEY] = arm;
    }

    try {
        sessionStorage.setItem(STORAGE_KEY, arm);
    } catch {
        // storage blocked: the form attribute still covers this page
    }
}

/**
 * Create-request params plus the arm this visitor was exposed to, if any. The
 * tab remembers the arm for any later form; only one with the AI trigger shows
 * the treatment (a parody form would join the cohort otherwise).
 */
export function withTrialFormArm<T extends Record<string, unknown>>(
    params: T,
    form: HTMLFormElement | null | undefined
): T {
    if (!form?.querySelector(AI_FIELD_SELECTOR)) return params;

    const arm = form.dataset[FORM_DATA_KEY] || storedArm();

    return arm ? { ...params, [FORM_FIELD]: arm } : params;
}

function storedArm(): string {
    try {
        return sessionStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
        return '';
    }
}

export function resetTrialGateForTest(): void {
    pending = null;
}
